import { useRouter } from 'next/router';
import { ConvertedProduct, ProductResponse } from '../types';

import { useAuth } from '@teko/auth-react';
import { CalculatedPrice } from '@teko/cart/v2';
import { ProductOptionsColumn, getParamsFromUrl } from '@teko/discovery';
import { formatCurrency } from '@teko/ds-desktop';
import { HomepageFilled } from '@teko/ds-icon';
import { logger } from '@teko/log';
import { PaymentMethodCode } from '@teko/payment';
import { InstallmentTerm } from '@teko/terra-bff';
import { cleanObject } from '@teko/utils';
import { LIMIT_ITEM_PER_PAGE_PRODUCT_LIST } from 'constants/index';
import {
  convertProductDetail,
  getFrame,
  isServerless,
  isVariantUrlGrouped,
  markUserIsOver18,
  needConfirm18YearsOld,
} from 'helpers';
import { t } from 'i18n';
import { Breadcrumb } from 'modules/search-page/shared';
import { useEffect, useMemo, useState } from 'react';
import {
  useCartV2,
  useFeatureFlags,
  useFrame,
  useServices,
  useTenantConfig,
} from '../contexts';
import { useCalculatePrice } from '../hooks';
import { useDiscoveryProduct, useSearch } from './useDiscovery';
import { useHandlerUnderEighteenYearOld } from './useHandlerUnderEighteenYearOld';
import { useProductRatingList } from './useProductRating';

export const useDetailPage = (serverProduct: ProductResponse) => {
  const {
    epicOmni976,
    isMobile,
    epicOmni1459,
    merchantInstallmentMethodCode,
    epicEcom495,
    passSkuToReturnSimilarProducts,
    displayNewLayoutForProductDetailSGC,
  } = useTenantConfig();
  const { flags } = useFeatureFlags();
  const { paymentBffService } = useServices();
  const [sku, setSku] = useState<string>(
    serverProduct?.product?.productInfo?.sku,
  );
  const [showPopupCheckAge, setShowModalCheckAge] = useState<boolean>();
  const [isUserUnder18, setIsUserUnder18] = useState<boolean>();
  const [
    showWarningPromotionWithInstallment,
    setShowWarningPromotionWithInstallment,
  ] = useState(false);
  const [minAmountByMonth, setMinAmountByMonth] = useState<number>();
  const [isReturnVariants, setIsReturnVariants] = useState(false);
  const { convertProduct } = useHandlerUnderEighteenYearOld({
    handleUnder18: () => setShowModalCheckAge(true),
  });
  const { loading: authLoading } = useAuth();

  const setShowPopupCheckAge = (value: boolean) => {
    setShowModalCheckAge(value);
    if (!value) {
      setIsUserUnder18(false);
      markUserIsOver18();
    }
  };

  const router = useRouter();
  const { frames } = useFrame();
  const {
    result,
    mutate: mutateProduct,
    isLoading,
  } = useDiscoveryProduct(
    sku || (router.query.sku as string),
    router.query.productId as string,
    {
      revalidateIfStale: true,
      fallbackData: serverProduct?.product,
    },
  );

  useEffect(() => {
    // use useEffect instead of swr onSuccess
    // since the later does not get called consistently
    if (!isLoading && result?.product && router.query.sku != sku) {
      const { sku: newSku, canonical } = result.product.productInfo;
      const queryParams = cleanObject({
        sku: newSku,
        productId: router.query.productId,
      });
      router.replace(
        {
          pathname: !router.query.productId
            ? '/products/[sku]'
            : '/products/detail/[productId]',
          query: queryParams,
        },
        {
          pathname: `/${canonical}`,
          query: isVariantUrlGrouped(canonical) ? { sku: newSku } : {},
        },
        {
          //In new detailPage SGC new layout, when replace router with new sku, always rerender the page.
          shallow: !displayNewLayoutForProductDetailSGC,
        },
      );
    }
  }, [
    isLoading,
    result?.product?.productInfo?.sku,
    displayNewLayoutForProductDetailSGC,
  ]);

  const { flattenItems } = useCartV2();
  // state to check if a product variant is being fetched
  // to be replaced with a global loader

  const product = useMemo(
    () => convertProductDetail(result.product),
    [result.product],
  );

  const defaultPrice = !isServerless()
    ? serverProduct.priceAndPromotions
    : convertDiscoveryPrice(
        product.price,
        product.status?.sellable
          ? serverProduct?.priceAndPromotions?.promotions
          : [],
      );

  /**
   * product from server is product in HCM City
   * if user is in other city, we need revalidate price and promotions
   */
  const {
    price,
    promotionBlocks,
    setPayloadAddToCart,
    selectPromotion,
    payloadAddToCart,
    promotionLoading,
    priceLoading,
    selectedPromotion,
    selectPromotionLoading,
    applicableInstallmentMethods,
    variants,
  } = useCalculatePrice(
    sku,
    defaultPrice,
    product?.status?.sellable,
    (router.query.selectPromotionId as string) ||
      getParamsFromUrl(router.asPath, 'selectPromotionId') ||
      '',
    isReturnVariants,
  );

  const searchRelatedProductsParams = passSkuToReturnSimilarProducts
    ? {
        page: 1,
        pageSize: LIMIT_ITEM_PER_PAGE_PRODUCT_LIST,
        filter: {
          skuToReturnSimilarProducts: sku,
        },
      }
    : !!product?.categories.length
    ? {
        page: 1,
        pageSize: LIMIT_ITEM_PER_PAGE_PRODUCT_LIST,
        filter: {
          excludedSkus: [product?.sku],
          categoryIds: [product?.categories[product?.categories.length - 1].id],
        },
      }
    : {};

  const { result: searchRelatedProductsResult } = useSearch(
    searchRelatedProductsParams,
  );

  // fallback to serverProduct if not found
  const relatedProducts =
    (searchRelatedProductsResult?.products || [])?.length > 0
      ? searchRelatedProductsResult?.products
      : serverProduct.relatedProducts;

  // EPIC-315: effect replace images when user is under 18 years old
  const { images, actionReady } = useMemo(() => {
    return convertProduct(product);
  }, [product?.categories, isUserUnder18, authLoading]);

  const productRatingData = useProductRatingList({ ...product, images });

  // backward compatibility for epic_ecom_315
  useEffect(() => {
    if (flags.epic_ecom_315) return;
    if (needConfirm18YearsOld(product?.isAlcoholicCategory || false)) {
      setShowPopupCheckAge(true);
    }
  }, [product?.sku, product?.isAlcoholicCategory]);

  const stockAvailable = product?.needManageStock
    ? product?.totalAvailable || 0
    : 999;

  const onSelectVariant = async (
    variant: ProductOptionsColumn,
  ): Promise<void> => {
    // if other sku
    const newSku = variant.sku;
    if (
      newSku === router.query.sku &&
      variant.quantity === payloadAddToCart.quantity
    )
      return;

    setPayloadAddToCart((prev) => {
      return {
        ...prev,
        // note: this logic is little confusing.
        // for variants that have no overlapping attributes with current sku
        // Discovery productOptions is returning 0 quantity
        // client would display this variant as a blurred option.
        quantity: variant.quantity === 0 ? 1 : variant.quantity,
        sku: variant.sku,
      };
    });

    if (newSku === router.query.sku) {
      await mutateProduct(
        {
          ...result.product,
          productOptions: {
            ...product.productOptions,
            rows: product.productOptions?.rows.map((row) => ({
              ...row,
              options: row.options.map((col) =>
                col.quantity === variant.quantity
                  ? { ...col, selected: true }
                  : { ...col, selected: false },
              ),
            })),
          },
        },
        { revalidate: false },
      );
    } else {
      setSku(newSku);
    }
  };
  const isCanAddToCard =
    product &&
    (product.isBundle ||
      passQuantityCheck(
        product?.needManageStock,
        stockAvailable,
        (flattenItems || []).find((item) => item.sku === product.sku)?.quantity,
      ));
  const isOutOfStock =
    product?.totalAvailable !== null &&
    product.totalAvailable !== undefined &&
    product?.totalAvailable <= 0;
  const showFlashSale = useMemo(() => {
    if (!product) return false;
    if (!product.flashSale) return false;
    const { total, used } = product.flashSale;
    const remain = Math.min(total - used, stockAvailable);
    return remain > 0;
  }, [product, stockAvailable]);

  const breadcrumbs: Breadcrumb = useMemo(() => {
    if (epicEcom495) {
      const homeBreadcrumb: Breadcrumb = [
        {
          link: '/',
          name: (
            <HomepageFilled
              size={16}
              color="placeholder"
              style={{ marginRight: '4px' }}
            />
          ),
          isLast: true, // add this to remove the arrow
        },
        {
          link: '/',
          name: t('Trang chủ'),
        },
      ];
      const productName = product?.displayName || product?.name || '';
      const breadcrumbs = product?.breadcrumbs?.length
        ? [...homeBreadcrumb, ...product.breadcrumbs]
        : [...homeBreadcrumb];

      return [
        ...breadcrumbs,
        {
          disabled: true,
          link: '#',
          name: productName,
        },
      ];
    }

    if (
      !serverProduct?.breadcrumbs ||
      serverProduct?.breadcrumbs?.length === 0 ||
      !epicOmni976
    ) {
      return isMobile || epicOmni976
        ? []
        : [
            {
              link: '/',
              name: t('Trang chủ'),
            },
            {
              disabled: true,
              link: '#',
              name: product?.displayName || product?.name || '',
            },
          ];
    }
    return [
      {
        link: '/',
        name: <HomepageFilled size={24} color="placeholder" />,
      },
      ...serverProduct.breadcrumbs,
    ];
  }, [epicOmni976, isMobile, epicEcom495]);

  const isAllowInstallmentPayment = useMemo(() => {
    return (
      !!epicOmni1459 &&
      serverProduct.priceAndPromotions?.availableInstallmentPaymentMethods?.includes(
        PaymentMethodCode.VNPAY_INSTALLMENT_CREDIT,
      )
    );
  }, [
    serverProduct.priceAndPromotions?.availableInstallmentPaymentMethods,
    epicOmni1459,
  ]);

  const selectedPromotionsNotApplyWithInstallment = useMemo(() => {
    return promotionBlocks
      .flatMap((promotionGroup) => promotionGroup.promotions)
      .filter(
        (promotion) =>
          promotion.isSelected && !!promotion.highlightedDescriptions?.length,
      );
  }, [promotionBlocks]);

  const getMinAmountByMonth = (terms: Array<InstallmentTerm>) => {
    const periods = terms.flatMap((t) =>
      t.paymentMethods.flatMap((paymentMethod) => paymentMethod.periods),
    );
    let minAmount;
    if (periods.length) {
      minAmount = periods[0].amountByMonth;
      for (let i = 0; i < periods.length; i++) {
        if (periods[i].amountByMonth < minAmount) {
          minAmount = periods[i].amountByMonth;
        }
      }
    }
    return minAmount;
  };

  const vnpayInstallmentCreditMethod = useMemo(() => {
    return !!applicableInstallmentMethods?.length
      ? applicableInstallmentMethods.find(
          (method) =>
            method.methodCode === PaymentMethodCode.VNPAY_INSTALLMENT_CREDIT,
        )
      : undefined;
  }, [applicableInstallmentMethods]);

  useEffect(() => {
    const getInstallmentTerms = async (
      merchantMethodCode: string,
      amount: number,
    ) => {
      try {
        const { terms } = await paymentBffService.getInstallmentTerms(
          merchantMethodCode,
          amount,
        );
        const minAmount = getMinAmountByMonth(terms);
        setMinAmountByMonth(minAmount);
      } catch (error) {
        logger.error('getInstallmentTerms error', error);
        window.track(
          'GetInstallmentTermsEvent',
          'getInstallmentTermsError',
          error,
        );
      }
    };
    // not call API when not allow purchase in installment (isAllowInstallmentPayment = false)
    if (
      isAllowInstallmentPayment &&
      merchantInstallmentMethodCode &&
      typeof vnpayInstallmentCreditMethod?.installmentPrice === 'number'
    ) {
      getInstallmentTerms(
        merchantInstallmentMethodCode,
        vnpayInstallmentCreditMethod.installmentPrice,
      );
    }
  }, [
    vnpayInstallmentCreditMethod?.installmentPrice,
    paymentBffService,
    merchantInstallmentMethodCode,
    isAllowInstallmentPayment,
  ]);

  return {
    isLoading,
    product: { ...product, images },
    primitiveProduct: result?.product || serverProduct.product,
    frame: getFrame(product?.tags || [], frames),
    onSelectVariant,
    stockAvailable,
    isOutOfStock,
    relatedProducts,
    isCanAddToCard,
    showFlashSale,
    setSelectedPromotion: selectPromotion,
    selectPromotionLoading,
    price,
    setPayloadAddToCart,
    payloadAddToCart,
    promotionBlocks,
    promotionLoading,
    priceLoading,
    selectedPromotion,
    showPopupCheckAge,
    setShowPopupCheckAge,
    actionReady,
    breadcrumbs: breadcrumbs || [],
    isAllowInstallmentPayment,
    showWarningPromotionWithInstallment,
    setShowWarningPromotionWithInstallment,
    selectedPromotionsNotApplyWithInstallment,
    minAmountByMonth,
    variants,
    setIsReturnVariants,
    productRatingData,
    serverRating: serverProduct.rating,
  };
};

const passQuantityCheck = (
  needManageStock: boolean,
  totalAvailable: number,
  quantity: number | undefined,
): boolean => {
  if (needManageStock && quantity !== undefined && quantity >= totalAvailable)
    return false;
  return true;
};

const convertDiscoveryPrice = (
  price: ConvertedProduct['price'],
  promotions: CalculatedPrice['promotions'] = [],
) => {
  if (!price || !price.price || !price.sellPrice) return null;
  return {
    price: price.price,
    discountPercent: price.discount ? `-${price.discount}%` : undefined,
    displayRowTotal: formatCurrency(price.price, { underline: true }),
    strikeThroughDisplayRowTotal: formatCurrency(
      price.supplierRetailPrice || price.sellPrice,
      {
        underline: true,
      },
    ),
    promotions: promotions,
  } as CalculatedPrice;
};
